import { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { LngLatBoundsLike } from "maplibre-gl";
import { Geometry } from "geojson";

import { CompareTableIcon } from "@/assets/icons";
import { Table } from "@/components/ui/table";
import { useMap } from "@/hooks/map/core/useMapContext";
import {
  BOUNDS_PADDING,
  TO_BOUNDS_DURATION
} from "@/hooks/map/interactions/useMapInteractions";
import { cn } from "@/lib/utils";
import { RootState } from "@/store";
import {
  removeComparisonFeatureByIndex,
  setComparisonFeature
} from "@/store/slice/mapSlice";
import { Dataset, DatasetAttributeSchema } from "@/types/dataset";
import { SelectedFeature } from "@/types/map";
import {
  transformPropertiesWithCategory,
  transformVisibleAttributes
} from "@/utils/dataset";

import { bbox } from "@turf/turf";

import ComparePanelHeader from "./ComparePanelHeader";
import ComparePanelValue from "./ComparePanelValue";

const transformFeatureProperties = (
  feature: SelectedFeature,
  selectedDatasetCategories: Dataset
) => {
  if (!feature) return {};

  const transformedProperties = transformPropertiesWithCategory(
    feature.properties as SelectedFeature,
    selectedDatasetCategories?.categories ?? []
  );

  const resultTransform = transformVisibleAttributes(transformedProperties);

  return transformedProperties.reduce<Record<string, unknown>>((acc, item) => {
    const transformedValue = resultTransform[item.key];
    acc[item.key] = transformedValue || item.value;
    return acc;
  }, {});
};

export default function ComparePanelData() {
  const dispatch = useDispatch();
  const { handleClearSelectedState, map, zoomLevel } = useMap();

  const { selectedDataset } = useSelector((root: RootState) => root.dataset);
  const { comparisonFeatureData } = useSelector((root: RootState) => root.map);

  const handlePinned = (index: number) => {
    if (comparisonFeatureData.length > 0 && index !== 0) {
      const updatedFeatures = [...comparisonFeatureData];
      const featureToPin = updatedFeatures.splice(index, 1)[0];
      updatedFeatures.unshift(featureToPin);
      dispatch(setComparisonFeature(updatedFeatures));
    }
  };

  const handleRemoveCurrentFeatureData = (index: number) => {
    handleClearSelectedState(comparisonFeatureData[index]);
    dispatch(removeComparisonFeatureByIndex(index));
  };

  const handleZoomToTarget = (geometry: Geometry) => {
    const bounds = bbox(geometry);
    map?.fitBounds(bounds as LngLatBoundsLike, {
      padding: BOUNDS_PADDING,
      maxZoom: zoomLevel,
      duration: TO_BOUNDS_DURATION
    });
  };

  const referenceIdAttribute = useMemo(() => {
    return (
      selectedDataset?.categories.find((attr) => attr.is_reference_id)
        ?.attribute_name || ""
    );
  }, [selectedDataset?.categories]);

  const attributes = useMemo(() => {
    return selectedDataset?.categories.filter(
      (attr) =>
        attr.attribute_name !== referenceIdAttribute &&
        attr.attribute_name !== "Grid ID" &&
        attr.attribute_name !== "Catchment ID"
    );
  }, [
    selectedDataset?.categories,
    referenceIdAttribute
  ]) as DatasetAttributeSchema[];

  const transformedFeatures = useMemo(() => {
    return comparisonFeatureData.map((feature, index) => {
      const transformed = transformFeatureProperties(
        feature,
        selectedDataset as Dataset
      );
      return {
        ...feature,
        ...feature.properties,
        ...transformed,
        index
      };
    });
  }, [comparisonFeatureData, selectedDataset]);

  return (
    <div
      className="w-full overflow-auto px-2 pb-3"
      data-testid="compare-panel-data"
    >
      <Table className="min-w-full table-fixed border-separate border-spacing-x-2">
        <thead>
          <tr>
            <th className="pl-2 w-full text-left text-xl font-semibold text-txtHeading leading-7 truncate">
              {selectedDataset?.data?.display_name || "Comparison Table"}
            </th>
            {transformedFeatures.map((feature, featureIndex) => (
              <th key={`head-${feature.id}`} className="w-full align-top">
                <ComparePanelHeader
                  referenceIdAttribute={referenceIdAttribute}
                  geometry={comparisonFeatureData[featureIndex]?.geometry}
                  value={feature}
                  isPinned={featureIndex === 0}
                  index={featureIndex}
                  handlePinned={handlePinned}
                  handleRemoveCurrentFeatureData={
                    handleRemoveCurrentFeatureData
                  }
                  handleZoomToTarget={handleZoomToTarget}
                />
              </th>
            ))}
            {transformedFeatures.length < 4 &&
              Array.from({ length: 4 - transformedFeatures.length }).map(
                (_, placeholderIndex) => (
                  <th
                    key={`head-placeholder-${transformedFeatures.length + placeholderIndex}`}
                    className="w-full"
                  />
                )
              )}
          </tr>
        </thead>
        <tbody>
          {attributes.map((attribute, attributeIndex) => (
            <tr key={`row-${attribute.id}-${attributeIndex}`}>
              <td className="text-sm font-semibold text-txtHeading w-full align-top">
                <div
                  key={`${attribute.id}-${attributeIndex}`}
                  className={cn(
                    "rounded-lg w-full flex flex-col h-[50px] truncate hover:text-clip hover:overflow-auto",
                    attributeIndex % 2 !== 0 && "bg-neutral-50"
                  )}
                >
                  <span className="text-sm font-semibold text-left text-txtHeading leading-5 py-3 px-2">
                    {attribute.attribute_name}
                  </span>
                </div>
              </td>
              {transformedFeatures.map((feature, featureIndex) => (
                <td
                  key={`val-${feature.id}-${featureIndex}`}
                  className="w-full align-top"
                >
                  <ComparePanelValue
                    geometry={feature.geometry}
                    value={feature}
                    attribute={attribute}
                    isPinned={featureIndex === 0}
                    index={featureIndex}
                    attributeIndex={attributeIndex}
                  />
                </td>
              ))}
              {transformedFeatures.length < 2 && attributeIndex === 0 && (
                <td
                  rowSpan={attributes.length}
                  colSpan={5 - transformedFeatures.length}
                  className="align-top text-center text-gray-400 italic"
                  style={{
                    minWidth: `${(4 - transformedFeatures.length) * 200}px`
                  }}
                >
                  <div className="flex flex-col items-center justify-center h-full pt-4">
                    <CompareTableIcon fill="#CECECE" />
                    <p className="text-[1.25rem] text-light-gray leading-8 text-center mt-2">
                      Please select point to grid as data comparison.
                    </p>
                  </div>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
}
